/**
 * MindMapCanvas
 * - Responsive card grid (fills available width)
 * - Collapsible outline sidebar showing full plan tree
 * - Smooth slide animation between levels
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext, PointerSensor, useSensor, useSensors,
  pointerWithin, DragOverlay,
  useDraggable, useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, rectSortingStrategy,
} from '@dnd-kit/sortable';
import SortableCard from './SortableCard';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, PanelLeftClose, PanelLeftOpen, Home, ListTodo, CheckSquare, Square, LayoutList, X, FileText, Loader, LockKeyhole } from 'lucide-react';
import BoxCard from './BoxCard';
import BoxEditor from './BoxEditor';
import CardSearch from './CardSearch';
import { API } from '../App';

const CARD_W   = 300;
const CARD_GAP = 24;


// How many columns fit in a given pixel width
function colsForWidth(px) {
  if (px < 400)  return 1;
  if (px < 700)  return 2;
  if (px < 1000) return 3;
  if (px < 1300) return 4;
  return Math.floor((px + CARD_GAP) / (CARD_W + CARD_GAP));
}

// ── Outline sidebar ───────────────────────────────────────────────────────────

// Shared ref so OutlineNode can write dropPos and handleOutlineDragEnd can read it
const _outlineDropPosRef = { current: null, targetId: null };

// Returns true if `ancestorId` is an ancestor of `nodeId` in allBoxes (cycle guard)
function isAncestorOf(ancestorId, nodeId, allBoxes) {
  let current = allBoxes.find(b => b.id === nodeId);
  while (current) {
    if (current.parent_id === ancestorId) return true;
    current = allBoxes.find(b => b.id === current.parent_id);
  }
  return false;
}

function OutlineNode({ box, allBoxes, depth, currentPath, onNavigate, onMoveBox, draggingId, isOwner, isMobileDevice }) {
  const children = allBoxes.filter(b => b.parent_id === box.id);
  const [open, setOpen] = useState(depth < 2);
  const [hovered, setHovered] = useState(false);
  const [dropPos, setDropPos] = useState(null); // 'before' | 'after' | 'into'
  const isActive = currentPath.includes(box.id);
  const isDragging = draggingId === box.id;

  // Make this node draggable
  const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
    id: box.id,
    disabled: !isOwner,
    data: { box },
  });

  // Make this node a drop target
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${box.id}`,
    disabled: !isOwner,
    data: { box },
  });

  // Combine drag + drop refs
  const setRef = (el) => { setDragRef(el); setDropRef(el); };

  // Track pointer position within the row for before/after/into decision
  function onPointerMove(e) {
    if (!draggingId || draggingId === box.id) return;
    if (isAncestorOf(draggingId, box.id, allBoxes)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const relY = e.clientY - rect.top;
    const pct = relY / rect.height;
    const pos = pct < 0.25 ? 'before' : pct > 0.75 ? 'after' : 'into';
    setDropPos(pos);
    // Write to shared ref so drag-end handler can read it
    _outlineDropPosRef.current = pos;
    _outlineDropPosRef.targetId = box.id;
  }

  function onPointerLeave() {
    setDropPos(null);
    // Keep the last zone until drag-end; the pointer can leave the row while
    // crossing its 1px edge without changing the displayed target.
  }

  const isInvalidTarget = draggingId && (
    draggingId === box.id || isAncestorOf(draggingId, box.id, allBoxes)
  );

  const showDropIndicator = isOver && !isInvalidTarget && dropPos;

  return (
    <div style={{ opacity: isDragging ? 0.35 : 1, transition: 'opacity 0.15s' }}>
      <div
        ref={setRef}
        data-outline-drop-id={box.id}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: `4px ${6 + depth * 14}px 4px ${4 + depth * 14}px`,
          borderRadius: 6,
          background: (showDropIndicator && dropPos === 'into')
            ? 'var(--bg-4)'
            : isActive ? 'var(--bg-4)' : 'transparent',
          outline: (showDropIndicator && dropPos === 'into') ? '2px solid var(--accent, #6366f1)' : 'none',
          outlineOffset: -2,
          cursor: 'pointer',
          userSelect: 'none',
          position: 'relative',
          transition: 'background 0.1s',
        }}
        onClick={() => {
          if (children.length > 0) setOpen(o => !o);
          onNavigate(box);
        }}
      >
        {showDropIndicator && dropPos === 'before' && <div style={{ position: 'absolute', top: 0, left: 6 + depth * 14, right: 6 + depth * 14, height: 2, background: 'var(--accent, #6366f1)', borderRadius: 1, pointerEvents: 'none' }} />}
        {showDropIndicator && dropPos === 'after' && <div style={{ position: 'absolute', bottom: 0, left: 6 + depth * 14, right: 6 + depth * 14, height: 2, background: 'var(--accent, #6366f1)', borderRadius: 1, pointerEvents: 'none' }} />}
        {/* Drag handle — always visible on mobile, hover-reveal on desktop */}
        {isOwner && (
          <span
            {...attributes}
            {...listeners}
            style={{
              opacity: isMobileDevice ? 0.4 : hovered ? 0.5 : 0,
              cursor: 'grab',
              color: 'var(--text-faint)',
              fontSize: 13,
              lineHeight: 1,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              padding: '2px 3px',
              marginRight: 1,
              transition: 'opacity 0.1s',
              touchAction: 'none',
            }}
            onClick={e => e.stopPropagation()}
            title="Drag to move"
          >
            ⠿
          </span>
        )}
        {children.length > 0 ? (
          open
            ? <ChevronDown size={11} style={{ flexShrink: 0, color: 'var(--text-faint)' }} />
            : <ChevronRight size={11} style={{ flexShrink: 0, color: 'var(--text-faint)' }} />
        ) : (
          <div style={{ width: 11, flexShrink: 0 }} />
        )}
        <span {...(isOwner ? { ...attributes, ...listeners } : {})} title={isOwner ? 'Drag card title to move' : undefined} style={{
          fontSize: 12, lineHeight: 1.4,
          color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
          fontWeight: isActive ? 600 : 400,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: 1,
          cursor: isOwner ? 'grab' : 'pointer', touchAction: isOwner ? 'none' : undefined,
        }}>
          {box.title || 'Untitled'}
        </span>
        {!!box.is_private && <LockKeyhole size={11} title="Private card and subcards" style={{ color: '#a16207', flexShrink: 0 }} />}
        {children.length > 0 && (
          <span style={{ fontSize: 10, color: 'var(--text-faint)', flexShrink: 0 }}>
            {children.length}
          </span>
        )}
      </div>
      {open && children.length > 0 && (
        <div>
          {children.map(child => (
            <OutlineNode
              key={child.id}
              box={child}
              allBoxes={allBoxes}
              depth={depth + 1}
              currentPath={currentPath}
              onNavigate={onNavigate}
              onMoveBox={onMoveBox}
              draggingId={draggingId}
              isOwner={isOwner}
              isMobileDevice={isMobileDevice}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryOutlineLink({ onOpenSummary, summaryStatus, color, mobile = false }) {
  return (
    <button
      type="button"
      onClick={onOpenSummary}
      title="View executive summary"
      style={{
        display: 'flex', alignItems: 'center', gap: mobile ? 8 : 6,
        width: 'calc(100% - 16px)', marginLeft: 16,
        padding: mobile ? '8px 10px' : '5px 8px',
        border: 'none', borderRadius: 6, background: 'transparent',
        color: 'var(--text-muted)', cursor: 'pointer', textAlign: 'left',
        fontFamily: 'inherit', fontSize: mobile ? 14 : 12, fontWeight: 600,
      }}
    >
      {summaryStatus === 'updating'
        ? <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }} style={{ display: 'flex', color }}><Loader size={mobile ? 15 : 13} /></motion.span>
        : <FileText size={mobile ? 15 : 13} style={{ flexShrink: 0 }} />}
      Executive Summary
    </button>
  );
}

function OutlineSidebar({ plan, allBoxes, currentPath, onNavigate, color, open, onToggle, shareRootId, tab, onTabChange, onTodoToggle, isOwner, onMoveBox, draggingId, isMobileDevice, onOpenSummary, summaryStatus }) {
  // For shared views, root of the outline = children of the share root box
  const rootBoxes = shareRootId
    ? allBoxes.filter(b => b.parent_id === shareRootId)
    : allBoxes.filter(b => !b.parent_id || b.parent_id === 'root');

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', position: 'relative', flexShrink: 0,
    }}>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="sidebar"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 220, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            style={{
              overflow: 'hidden', height: '100%',
              background: 'var(--bg-2)',
              borderRight: '1px solid var(--border)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Tab bar */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              {[
                { id: 'outline', label: 'Outline', Icon: Home },
                { id: 'todo',    label: 'TODO',    Icon: ListTodo },
              ].map(({ id, label, Icon }) => {
                const active = (tab || 'outline') === id;
                const todoCount = id === 'todo' ? allBoxes.filter(b => b.is_todo).length : 0;
                return (
                  <button
                    key={id}
                    onClick={() => onTabChange(id)}
                    style={{
                      flex: 1, padding: '9px 4px', border: 'none', background: 'transparent',
                      borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 5, fontFamily: 'inherit',
                      color: active ? 'var(--text-primary)' : 'var(--text-muted)',
                      fontWeight: active ? 700 : 500, fontSize: 11,
                      transition: 'color 0.15s, border-color 0.15s',
                    }}
                  >
                    <Icon size={12} />
                    {label}
                    {id === 'todo' && todoCount > 0 && (
                      <span style={{
                        background: color, color: '#fff', borderRadius: 8,
                        fontSize: 9, fontWeight: 700, padding: '1px 5px', lineHeight: 1.6,
                        minWidth: 14, textAlign: 'center',
                      }}>{todoCount}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Outline tab */}
            {(tab || 'outline') === 'outline' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 4px' }}>
                {!shareRootId && (
                  <div
                    onClick={() => onNavigate(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                      background: currentPath.length === 0 ? 'var(--bg-4)' : 'transparent',
                      marginBottom: 2,
                    }}
                  >
                    <Home size={13} style={{ flexShrink: 0, color: currentPath.length === 0 ? 'var(--text-primary)' : 'var(--text-muted)' }} />
                    <span style={{
                      fontSize: 12, fontWeight: currentPath.length === 0 ? 700 : 600,
                      color: currentPath.length === 0 ? 'var(--text-primary)' : 'var(--text-muted)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {plan.title}
                    </span>
                  </div>
                )}
                {isOwner && !(plan.role === 'editor' && plan.has_private_cards) && <SummaryOutlineLink onOpenSummary={onOpenSummary} summaryStatus={summaryStatus} color={color} />}
                <div style={{ height: 1, background: 'var(--border)', margin: '4px 8px 6px' }} />
                {rootBoxes.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--text-faint)', padding: '12px 10px' }}>{shareRootId ? 'No subcards' : 'No cards yet'}</div>
                ) : rootBoxes.map(box => (
                  <OutlineNode
                    key={box.id}
                    box={box}
                    allBoxes={allBoxes}
                    depth={0}
                    currentPath={currentPath}
                    onNavigate={onNavigate}
                    onMoveBox={onMoveBox}
                    draggingId={draggingId}
                    isOwner={isOwner}
                    isMobileDevice={isMobileDevice}
                  />
                ))}
              </div>
            )}

            {/* TODO tab */}
            {tab === 'todo' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 4px' }}>
                {(() => {
                  const todos = allBoxes.filter(b => b.is_todo);
                  if (todos.length === 0) return (
                    <div style={{ padding: '24px 12px', textAlign: 'center' }}>
                      <ListTodo size={24} style={{ color: 'var(--text-faint)', margin: '0 auto 8px', display: 'block' }} />
                      <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>No TODO items yet</div>
                      <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>Open a card menu and mark it as TODO</div>
                    </div>
                  );
                  return todos.map(box => (
                    <div
                      key={box.id}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8,
                        padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
                        margin: '1px 4px',
                        background: 'transparent',
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-4)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {isOwner && onTodoToggle && (
                        <button
                          onClick={e => { e.stopPropagation(); onTodoToggle(box); }}
                          title="Mark complete"
                          style={{
                            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            color: color, display: 'flex', flexShrink: 0, marginTop: 1,
                          }}
                        >
                          <CheckSquare size={14} />
                        </button>
                      )}
                      <div
                        onClick={() => onNavigate(box)}
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        <div style={{
                          fontSize: 12, color: 'var(--text-primary)', fontWeight: 500,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          lineHeight: 1.4,
                        }}>
                          {box.title || 'Untitled'}
                        </div>
                        {(() => {
                          const parent = box.parent_id ? allBoxes.find(b => b.id === box.parent_id) : null;
                          return parent ? (
                            <div style={{
                              fontSize: 10, color: 'var(--text-faint)', fontWeight: 400,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              marginTop: 1,
                            }}>
                              in {parent.title || 'Untitled'}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main canvas ────────────────────────────────────────────────────────────────
export default function MindMapCanvas({ plan, token, clientId, cardLocks = {}, isOwner, color, aiSidebarRef, initialFocusId, shareToken, shareRootId, onLevelChange, searchOpen, setSearchOpen, searchButtonRef, compactView, onOpenSummary, summaryStatus }) {
  const [levelStack, setLevelStack]   = useState([]);
  const currentParentIdRef = useRef(null); // always current, no stale closures
  const [currentBoxes, setCurrentBoxes] = useState([]);
  const [allBoxes, setAllBoxes]         = useState([]);
  const [editing, setEditing]           = useState(null);
  const [editError, setEditError]       = useState('');
  const [lockNotice, setLockNotice]     = useState('');
  const [uploadNotice, setUploadNotice] = useState('');
  const [transitioning, setTransitioning] = useState(false);
  const [sidebarOpen, setSidebarOpen]   = useState(true);
  const [sidebarTab, setSidebarTab]     = useState('outline'); // 'outline' | 'todo'
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isMobile = window.innerWidth < 768;
  const [containerWidth, setContainerWidth] = useState(isMobile ? window.innerWidth : window.innerWidth - 220);
  const navigate   = useNavigate();
  const location = useLocation();
  // The router is the source of truth, including browser Back/Forward.
  const focusMatch = location.pathname.match(/^\/p\/[^/]+\/(?:focus|b)\/([^/]+)\/?$/);
  const routeFocusId = shareToken ? initialFocusId : (focusMatch?.[1] || null);
  const planId = plan.id;
  const hierarchyPaths = location.state?.hierarchyPaths;
  const navigationOperation = useRef(0);
  React.useLayoutEffect(() => {
    // A committed route wins over every request started on the previous route.
    navigationOperation.current += 1;
    setTransitioning(false);
    return () => { navigationOperation.current += 1; };
  }, [location, planId, shareToken, shareRootId, initialFocusId]);

  function beginNavigation() {
    const operation = ++navigationOperation.current;
    setTransitioning(true);
    return operation;
  }

  function finishNavigation(operation) {
    setTimeout(() => {
      if (operation === navigationOperation.current) setTransitioning(false);
    }, 350);
  }

  const navigateLevel = useCallback((stack, options = {}) => {
    const paths = [`/p/${planId}`, ...stack.map(level => `/p/${planId}/focus/${level.box.id}`)];
    const target = paths[paths.length - 1];
    if (location.pathname === target && hierarchyPaths?.join('\n') === paths.join('\n')) return;
    const visit = (i, replace = false) => navigate(paths[i], {
      replace,
      state: { hierarchyPaths: paths.slice(0, i + 1), parentPath: paths[Math.max(0, i - 1)] },
    });
    // Materialize missing ancestors synchronously. A direct link or outline jump
    // must have one real history entry per parent, even before data finishes loading.
    const currentIndex = paths.indexOf(location.pathname);
    if (currentIndex >= 0 && currentIndex < paths.length - 1) {
      visit(currentIndex, true);
      for (let i = currentIndex + 1; i < paths.length; i++) visit(i);
    } else if (location.pathname === target && hierarchyPaths) {
      visit(paths.length - 1, true);
    } else {
      visit(0, !!options.replace);
      for (let i = 1; i < paths.length; i++) visit(i);
    }
  }, [navigate, planId, location.pathname, hierarchyPaths]);

  // ── Toggle todo on a box ──────────────────────────────────────────────────
  async function handleTodoToggle(box) {
    const newVal = !box.is_todo;
    const res = await fetch(`${API}/api/boxes/${box.id}/todo`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ is_todo: newVal }),
    });
    if (!res.ok) { const result = await res.json().catch(() => ({})); setLockNotice(result.error || 'Could not update card'); return; }
    const updated = await res.json();
    // Update allBoxes so sidebar reflects immediately
    setAllBoxes(prev => prev.map(b => b.id === box.id ? { ...b, is_todo: newVal } : b));
    setCurrentBoxes(prev => prev.map(b => b.id === box.id ? { ...b, is_todo: newVal } : b));
  }

  // ── Fetch full tree (for outline sidebar) — declared first to avoid TDZ ────
  // cache: 'no-store' — without this, some browsers/proxies can serve a cached GET
  // response right after a PUT/POST to the same origin, showing stale card data
  // until a manual hard refresh.
  const fetchAll = useCallback(async () => {
    const qs = shareToken ? `?shareToken=${shareToken}` : ``;
    const res = await fetch(`${API}/api/plans/${plan.id}/tree${qs}`, { cache: 'no-store', headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const boxes = await res.json();
    setAllBoxes(boxes);
    return boxes;
  }, [plan.id, shareToken, token]);

  // ── Fetch children of a parent ────────────────────────────────────────────────
  const fetchBoxes = useCallback(async (parentId) => {
    const scope = parentId || 'root';
    const qs2 = shareToken ? `?shareToken=${shareToken}` : ``;
    const res = await fetch(`${API}/api/plans/${plan.id}/boxes/${scope}/children${qs2}`, { cache: 'no-store', headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) { const error = new Error('Card level unavailable'); error.status = res.status; throw error; }
    return res.json();
  }, [plan.id, shareToken, token]);

  // Lightweight shared-plan sync. Local editors keep their drafts; the visible cards and
  // outline refresh when another collaborator saves, without running any AI work.
  useEffect(() => {
    if (!isOwner || !token || shareToken) return;
    let cancelled = false;
    const refresh = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const [visible, treeResponse] = await Promise.all([fetchBoxes(currentParentIdRef.current), fetch(`${API}/api/plans/${plan.id}/tree`, { cache: 'no-store', headers: { Authorization: `Bearer ${token}` } })]);
        if (!treeResponse.ok) return;
        const tree = await treeResponse.json();
        if (cancelled) return;
        const changed = (previous, next) => previous.length !== next.length || previous.some((box, i) => box.id !== next[i].id || box.updated_at !== next[i].updated_at || box.span !== next[i].span || box.compact_summary_status !== next[i].compact_summary_status || box.compact_summary !== next[i].compact_summary || box.content !== next[i].content);
        setCurrentBoxes(previous => changed(previous, visible) ? visible : previous);
        setAllBoxes(previous => changed(previous, tree) ? tree : previous);
        const byId = new Map(tree.map(box => [box.id, box]));
        setLevelStack(previous => {
          const next = previous.filter(level => byId.has(level.box.id)).map(level => {
            const fresh = byId.get(level.box.id);
            return fresh.updated_at !== level.box.updated_at || fresh.compact_summary !== level.box.compact_summary || fresh.content !== level.box.content
              ? { ...level, box: fresh } : level;
          });
          return next.length === previous.length && next.every((level, i) => level === previous[i]) ? previous : next;
        });
      } catch (error) {
        if (error.status === 404 && !cancelled) {
          try {
            const root = await fetchBoxes(null);
            setCurrentBoxes(root);
            setLevelStack([]);
            await fetchAll();
            navigateLevel([], { replace: true });
          } catch {}
        }
      }
    };
    const timer = setInterval(refresh, 3000);
    window.addEventListener('focus', refresh);
    return () => { cancelled = true; clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [isOwner, token, shareToken, fetchBoxes, fetchAll, plan.id, navigateLevel]);

  async function beginEdit(box) {
    if (box._parentId) { setEditError(''); setEditing(box); return; }
    try {
      const response = await fetch(`${API}/api/boxes/${box.id}/edit-lock`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not open card for editing');
      setEditError('');
      setLockNotice('');
      setEditing(result.box); // Always start from the server's latest card, not a stale canvas copy.
    } catch (error) { setLockNotice(error.message); }
  }

  useEffect(() => {
    if (!editing?.id || !token || !clientId) return;
    const id = editing.id;
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    const renew = async () => {
      try {
        const response = await fetch(`${API}/api/boxes/${id}/edit-lock`, { method: 'POST', headers,
          body: JSON.stringify({ clientId }) });
        if (!response.ok) setEditError('Your edit lock expired or the card was removed. Copy your draft before closing.');
      } catch { setEditError('Connection lost. Your draft is still open; check your connection before saving.'); }
    };
    const timer = setInterval(renew, 10000);
    return () => {
      clearInterval(timer);
      fetch(`${API}/api/boxes/${id}/edit-lock`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}`, 'X-IdeaCanvas-Client': clientId }, keepalive: true }).catch(() => {});
    };
  }, [editing?.id, token, clientId]);

  // Derived — must be before any useEffect that references it
  const parentBox = levelStack.length > 0 ? levelStack[levelStack.length - 1].box : null;

  // Level change: notify parent + wire sidebar refresh callback
  useEffect(() => {
    const parentId = parentBox?.id || null;
    currentParentIdRef.current = parentId; // keep ref in sync
    onLevelChange?.(parentBox ?? null, levelStack);
    if (aiSidebarRef?.current) {
      // Use ref so callback always reads the live parent, never a stale closure
      const cb = async (updatedCard) => {
        const pid = currentParentIdRef.current;
        if (updatedCard) {
          setCurrentBoxes(previous => previous.map(box => box.id === updatedCard.id ? updatedCard : box));
          setLevelStack(previous => previous.map(level =>
            level.box.id === updatedCard.id ? { ...level, box: updatedCard } : level
          ));
        }
        const [refreshed] = await Promise.all([fetchBoxes(pid), fetchAll()]);
        setCurrentBoxes(refreshed);
      };
      aiSidebarRef.current._onCardCreated = cb;
      if (typeof window !== 'undefined') window.__testOnCardCreated = cb;
    }
  }, [parentBox, fetchBoxes, fetchAll]);

  // Keyboard shortcut: N = new card (when not typing in an input)
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'n' && e.key !== 'N') return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable;
      if (isEditable) return;
      e.preventDefault();
      setEditing({ _parentId: parentBox?.id || 'root' });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [parentBox]);

  // Tab title
  useEffect(() => {
    const level = levelStack.length > 0 ? levelStack[levelStack.length - 1].box.title : null;
    document.title = level ? `${level} — ${plan.title} — IdeaCanvas` : `${plan.title} — IdeaCanvas`;
    return () => { document.title = 'IdeaCanvas'; };
  }, [plan.title, levelStack]);

  // ── Measure container width for responsive columns ──────────────────────────
  const containerRef = React.useRef(null);
  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // ── Load the current route; discard responses for levels already left ────────
  useEffect(() => {
    let cancelled = false;
    const operation = navigationOperation.current;
    const isCurrent = () => !cancelled && operation === navigationOperation.current;
    (async () => {
      const tree = await fetchAll(); // load full tree for outline
      if (!isCurrent()) return;
      if (shareRootId) {
        // A shared subtree starts on its root card, not one level above it.
        // Leaf shares still need to show the root card's own content.
        const root = tree.find(box => box.id === shareRootId);
        if (root) {
          const children = await fetchBoxes(shareRootId);
          if (!isCurrent()) return;
          setLevelStack([{ box: root, boxes: [] }]);
          setCurrentBoxes(children);
          return;
        }
      }
      if (routeFocusId) {
        try {
          const pathQs = shareToken ? `?shareToken=${shareToken}` : ``;
          const pathRes = await fetch(`${API}/api/boxes/${routeFocusId}/path${pathQs}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
          if (!isCurrent()) return;
          const path = await pathRes.json();
          if (!isCurrent()) return;
          const stack = [];
          let parentId = null;
          for (const ancestor of path) {
            const boxes = await fetchBoxes(parentId);
            if (!isCurrent()) return;
            const box = boxes.find(b => b.id === ancestor.id);
            if (box) stack.push({ box, boxes });
            parentId = ancestor.id;
          }
          const targetBoxes = await fetchBoxes(routeFocusId);
          if (!isCurrent()) return;
          setLevelStack(stack);
          setCurrentBoxes(targetBoxes);
          if (!shareToken) navigateLevel(stack, { replace: true });
          return;
        } catch { /* fall through */ }
      }
      if (!isCurrent()) return;
      const boxes = await fetchBoxes(null);
      if (!isCurrent()) return;
      setLevelStack([]);
      setCurrentBoxes(boxes);
    })();
    return () => { cancelled = true; };
  }, [plan.id, shareRootId, routeFocusId, shareToken, token, fetchAll, fetchBoxes, navigateLevel]);

  // Existing cards are summarized on first compact view. While background
  // generation runs, refresh the visible cards and focused parent in place.
  useEffect(() => {
    if (!compactView || !isOwner || !token || (plan.role === 'editor' && plan.has_private_cards)) return;
    const summaryScope = parentBox?.id || 'root';
    const summaryQuery = `?parentId=${encodeURIComponent(summaryScope)}`;
    let cancelled = false;
    let timer;
    let wasGenerating = false;
    let firstPoll = true;
    async function poll() {
      try {
        const response = await fetch(`${API}/api/plans/${plan.id}/card-summaries/status${summaryQuery}`, {
          headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
        });
        if (response.ok) {
          const status = await response.json();
          const generating = !!(status.pending || status.updating);
          if (!cancelled && (firstPoll || generating || wasGenerating)) {
            const parentId = currentParentIdRef.current;
            const [visible, focusedResponse] = await Promise.all([
              fetchBoxes(parentId),
              parentId ? fetch(`${API}/api/boxes/${parentId}`, { cache: 'no-store', headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve(null),
            ]);
            const focused = focusedResponse?.ok ? await focusedResponse.json() : null;
            if (!cancelled && currentParentIdRef.current === parentId) {
              setCurrentBoxes(visible);
              if (focused) setLevelStack(previous => previous.map(level =>
                level.box.id === focused.id ? { ...level, box: { ...level.box, ...focused } } : level
              ));
            }
          }
          wasGenerating = generating;
          firstPoll = false;
        }
      } catch (error) { console.error('[Card Summary] refresh failed:', error); }
      if (!cancelled) timer = setTimeout(poll, 4000);
    }
    fetch(`${API}/api/plans/${plan.id}/card-summaries/backfill`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId: summaryScope }),
    }).then(() => { if (!cancelled) poll(); }).catch(() => { if (!cancelled) poll(); });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [compactView, isOwner, token, plan.id, plan.role, plan.has_private_cards, parentBox?.id, fetchBoxes]);

  // ── Drill into a card ─────────────────────────────────────────────────────────
  async function drillInto(box) {
    if (transitioning) return;
    const operation = beginNavigation();
    const childBoxes = await fetchBoxes(box.id);
    if (operation !== navigationOperation.current) return;
    const newStack = [...levelStack, { box, boxes: currentBoxes }];
    setLevelStack(newStack);
    setCurrentBoxes(childBoxes);
    if (!shareToken) navigateLevel(newStack);
    finishNavigation(operation);
  }

  // ── Navigate from outline: jump to any box's children ────────────────────────
  async function navigateToBox(box) {
    // null = home/root level; block shared viewers from going above their root
    if (!box) {
      if (shareRootId) return; // shared viewers have no "home" above their root
      const operation = beginNavigation();
      const boxes = await fetchBoxes(null);
      if (operation !== navigationOperation.current) return;
      setLevelStack([]);
      setCurrentBoxes(boxes);
      if (!shareToken) navigateLevel([]);
      finishNavigation(operation);
      return;
    }
    if (transitioning) return;
    const operation = beginNavigation();

    // Build stack path to this box
    try {
      const pathQs = shareToken ? `?shareToken=${shareToken}` : ``;
      const pathRes = await fetch(`${API}/api/boxes/${box.id}/path${pathQs}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (operation !== navigationOperation.current) return;
      const path = await pathRes.json();
      if (operation !== navigationOperation.current) return;
      const sharedRoot = shareRootId ? allBoxes.find(b => b.id === shareRootId) : null;
      const stack = sharedRoot ? [{ box: sharedRoot, boxes: [] }] : [];
      let parentId = sharedRoot?.id || null;
      for (const ancestor of path) {
        if (ancestor.id === shareRootId) continue;
        const boxes = await fetchBoxes(parentId);
        if (operation !== navigationOperation.current) return;
        const found = boxes.find(b => b.id === ancestor.id);
        if (found) stack.push({ box: found, boxes });
        parentId = ancestor.id;
      }
      const childBoxes = await fetchBoxes(box.id);
      if (operation !== navigationOperation.current) return;
      setLevelStack(stack);
      setCurrentBoxes(childBoxes);
      if (!shareToken) navigateLevel(stack);
    } catch {
      if (operation !== navigationOperation.current) return;
      // Just show its children directly
      const childBoxes = await fetchBoxes(box.id);
      if (operation !== navigationOperation.current) return;
      const sharedRoot = shareRootId ? allBoxes.find(b => b.id === shareRootId) : null;
      setLevelStack(sharedRoot && sharedRoot.id !== box.id
        ? [{ box: sharedRoot, boxes: [] }, { box, boxes: currentBoxes }]
        : [{ box, boxes: currentBoxes }]);
      setCurrentBoxes(childBoxes);
    }
    finishNavigation(operation);
  }

  // ── Go back ───────────────────────────────────────────────────────────────────
  async function goBack() {
    if (transitioning || levelStack.length <= (shareRootId ? 1 : 0)) return;
    const operation = beginNavigation();
    const newStack = levelStack.slice(0, -1);
    const parentId = newStack.length > 0 ? newStack[newStack.length - 1].box.id : null;
    const refreshed = await fetchBoxes(parentId);
    if (operation !== navigationOperation.current) return;
    setLevelStack(newStack);
    setCurrentBoxes(refreshed);
    // In-app Back follows the hierarchy, not the previously visited page.
    if (!shareToken) {
      if (newStack.length > 0) {
        navigateLevel(newStack);
      } else {
        navigateLevel([]);
      }
    }
    finishNavigation(operation);
  }

  // ── Delete ────────────────────────────────────────────────────────────────────
  const authHeaders = token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' };

  async function handleDelete(boxId) {
    const response = await fetch(`${API}/api/boxes/${boxId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) { const result = await response.json().catch(() => ({})); setLockNotice(result.error || 'Could not delete card'); return; }
    const parentId = levelStack.length > 0 ? levelStack[levelStack.length - 1].box.id : null;
    const [refreshed] = await Promise.all([fetchBoxes(parentId), fetchAll()]);
    setCurrentBoxes(refreshed);
  }

  // ── Save ──────────────────────────────────────────────────────────────────────
  async function handleSave(data) {
    const parentId = editing?._parentId;
    const editingId = editing?.id;
    let saveFailed = false;
    let savedErrorMsg = '';
    try {
      if (editingId) {
        const res = await fetch(`${API}/api/boxes/${editingId}`, {
          method: 'PUT', headers: { ...authHeaders, 'X-IdeaCanvas-Client': clientId },
          body: JSON.stringify({ ...data, expected_updated_at: editing.updated_at }),
        });
        if (!res.ok) {
          saveFailed = true;
          const body = await res.json().catch(() => ({}));
          savedErrorMsg = body.error || `Server returned ${res.status}`;
        }
      } else {
        const res = await fetch(`${API}/api/plans/${plan.id}/boxes`, {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ ...data, parent_id: (parentId && parentId !== 'root') ? parentId : null }),
        });
        if (!res.ok) {
          saveFailed = true;
          const body = await res.json().catch(() => ({}));
          savedErrorMsg = body.error || `Server returned ${res.status}`;
        }
      }
    } catch (err) {
      saveFailed = true;
      savedErrorMsg = err.message || 'Network error';
    }
    if (saveFailed) {
      console.error('Card save failed:', savedErrorMsg);
      setEditError(`Save failed: ${savedErrorMsg}. Your draft is still open.`);
      return;
    }
    setEditError('');
    setEditing(null);
    // Confirm with server (always refresh so UI reflects true server state, even on failure).
    // Also refresh allBoxes (outline sidebar + other views read from this), not just
    // currentBoxes — previously only currentBoxes was updated here, so edits appeared
    // to "not save" anywhere that reads from allBoxes until a manual page refresh.
    const currentParentId = levelStack.length > 0 ? levelStack[levelStack.length - 1].box.id : null;
    const [refreshed, allRefreshed] = await Promise.all([fetchBoxes(currentParentId), fetchAll()]);
    setCurrentBoxes(refreshed);
    setAllBoxes(allRefreshed);
  }


  const currentPath = levelStack.map(l => l.box.id);
  const cols       = isMobile ? 1 : colsForWidth(containerWidth - 96);
  const levelKey   = levelStack.map(l => l.box.id).join('/') || 'root';
  const goingDeeper = levelStack.length > 0;
  const [dropOver, setDropOver] = useState(false);

  // Always clear the drop-over highlight when any drag ends anywhere on the page
  useEffect(() => {
    const clear = () => setDropOver(false);
    // dragleave on document fires when cursor leaves the window entirely or drop completes
    const onDocDragLeave = (e) => { if (!e.relatedTarget) setDropOver(false); };
    // Use capture=true so card's stopPropagation (bubble phase) can't block these
    document.addEventListener('dragend', clear, true);
    document.addEventListener('drop', clear, true);
    document.addEventListener('dragleave', onDocDragLeave, true);
    return () => {
      document.removeEventListener('dragend', clear, true);
      document.removeEventListener('drop', clear, true);
      document.removeEventListener('dragleave', onDocDragLeave, true);
    };
  }, []);
  const [activeId, setActiveId] = useState(null);
  const [outlineDraggingId, setOutlineDraggingId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // ── Move a box to a new parent (outline drag-and-drop) ────────────────────
  async function handleOutlineDragEnd({ active, over }) {
    setOutlineDraggingId(null);
    const targetId = over?.id?.replace(/^drop-/, '');
    const placement = _outlineDropPosRef.targetId === targetId ? _outlineDropPosRef.current : null;
    _outlineDropPosRef.current = null;
    _outlineDropPosRef.targetId = null;
    if (!active || !targetId || !placement || active.id === targetId || isAncestorOf(active.id, targetId, allBoxes)) return;
    try {
      const response = await fetch(`${API}/api/plans/${plan.id}/boxes/move`, {
        method: 'POST',
        headers: { ...authHeaders, 'X-IdeaCanvas-Client': clientId },
        body: JSON.stringify({ boxId: active.id, targetId, placement }),
      });
      if (!response.ok) { const result = await response.json().catch(() => ({})); throw new Error(result.error || 'Could not move card'); }
    } catch (error) { setLockNotice(error.message); }
    const currentParentId = levelStack.length > 0 ? levelStack[levelStack.length - 1].box.id : null;
    const [refreshed] = await Promise.all([fetchBoxes(currentParentId), fetchAll()]);
    setCurrentBoxes(refreshed);
  }

  // ── Reorder cards ─────────────────────────────────────────────────────────
  async function handleDragEnd({ active, over }) {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIdx = currentBoxes.findIndex(b => b.id === active.id);
    const newIdx = currentBoxes.findIndex(b => b.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = [...currentBoxes];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    const withPos = reordered.map((b, i) => ({ ...b, position: i }));
    setCurrentBoxes(withPos);
    const response = await fetch(`${API}/api/plans/${plan.id}/boxes/reorder`, {
      method: 'POST', headers: authHeaders,
      body: JSON.stringify({ order: withPos.map(b => ({ id: b.id, position: b.position })) }),
    });
    if (!response.ok) { const result = await response.json().catch(() => ({})); setLockNotice(result.error || 'Could not reorder cards'); setCurrentBoxes(await fetchBoxes(parentBox?.id || null)); }
  }

  // ── Toggle span ───────────────────────────────────────────────────────────────
  async function toggleSpan(box) {
    const newSpan = (box.span || 1) >= 2 ? 1 : 2;
    const updated = { ...box, span: newSpan };
    setCurrentBoxes(prev => prev.map(b => b.id === box.id ? updated : b));
    const response = await fetch(`${API}/api/boxes/${box.id}`, {
      method: 'PUT', headers: authHeaders,
      body: JSON.stringify({ span: newSpan }),
    });
    if (!response.ok) { const result = await response.json().catch(() => ({})); setLockNotice(result.error || 'Could not resize card'); setCurrentBoxes(await fetchBoxes(parentBox?.id || null)); }
  }

  // ── Canvas-level drag/drop to create a new card ──────────────────────────────
  function onCanvasDragOver(e) {
    if (!isOwner) return; // read-only: don't show drop UI at all
    const types = [...e.dataTransfer.types];
    // Don't intercept thumbnail reorders or AI image drags — let card handle those
    if (types.includes('text/x-thumb-index') || types.includes('text/x-ideacanvas-image-url')) return;
    // Don't intercept if hovering over a card — let the card's own drop handler take it
    if (e.target.closest('[data-card]')) { setDropOver(false); return; }
    if (types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDropOver(true);
    }
  }
  function onCanvasDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropOver(false);
  }
  async function onCanvasDrop(e) {
    if (!isOwner) return; // read-only: ignore drops entirely
    const types = [...e.dataTransfer.types];
    // Don't intercept thumbnail reorders
    if (types.includes('text/x-thumb-index') || types.includes('text/x-ideacanvas-image-url')) return;
    e.preventDefault();
    setDropOver(false);
    const files = [...e.dataTransfer.files];
    if (!files.length) return;
    const currentParentId = parentBox?.id || null;
    setUploadNotice(`Uploading ${files.length} file${files.length === 1 ? '' : 's'}…`);
    const failures = [];
    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      if (isImage) {
        const form = new FormData();
        form.append('file', file);
        try {
          const upload = await fetch(`${API}/api/upload`, { method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {} });
          const { url, error } = await upload.json();
          if (!upload.ok || !url) throw new Error(error || 'Image upload failed');
          const absUrl = url.startsWith('/') ? `${API}${url}` : url;
          const create = await fetch(`${API}/api/plans/${plan.id}/boxes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ parent_id: currentParentId || null, title: file.name.replace(/\.[^.]+$/, ''), content_type: 'image', content: absUrl }),
          });
          if (!create.ok) throw new Error((await create.json()).error || 'Could not create image card');
        } catch (err) { failures.push(`${file.name}: ${err.message}`); }
      } else {
        const form = new FormData();
        form.append('file', file);
        form.append('parentId', currentParentId || 'root');
        try {
          const upload = await fetch(`${API}/api/plans/${plan.id}/card-files`, {
          method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
          });
          if (!upload.ok) throw new Error((await upload.json()).error || 'File upload failed');
        } catch (err) { failures.push(`${file.name}: ${err.message}`); }
      }
    }
    setUploadNotice('');
    if (failures.length) setLockNotice(failures.join(' · '));
    // Refresh current level
    const refreshed = await fetchBoxes(currentParentId);
    setCurrentBoxes(refreshed);
    await fetchAll();
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', overflow: 'hidden', background: 'var(--bg)' }}>

      {/* ── Outline sidebar — desktop only ───────────────────────────────────── */}
      {!isMobile && (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={({ active }) => { _outlineDropPosRef.current = null; _outlineDropPosRef.targetId = null; setOutlineDraggingId(active.id); }}
          onDragEnd={handleOutlineDragEnd}
          onDragCancel={() => { _outlineDropPosRef.current = null; _outlineDropPosRef.targetId = null; setOutlineDraggingId(null); }}
        >
          <OutlineSidebar
            plan={plan}
            allBoxes={allBoxes}
            currentPath={currentPath}
            onNavigate={navigateToBox}
            color={color}
            open={sidebarOpen}
            onToggle={() => setSidebarOpen(o => !o)}
            shareRootId={shareRootId || null}
            tab={sidebarTab}
            onTabChange={setSidebarTab}
            onTodoToggle={handleTodoToggle}
            isOwner={isOwner}
            onMoveBox={handleOutlineDragEnd}
            draggingId={outlineDraggingId}
            isMobileDevice={false}
            onOpenSummary={onOpenSummary}
            summaryStatus={summaryStatus}
          />
        </DndContext>
      )}

      {/* ── Sidebar toggle — desktop only, bottom left ──────────────────── */}
      {!isMobile && (
        <button
          onClick={() => setSidebarOpen(o => !o)}
          title={sidebarOpen ? 'Collapse outline' : 'Show outline'}
          style={{
            position: 'fixed', bottom: 24, left: 16, zIndex: 200,
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--bg-3)', border: '1px solid var(--border)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-muted)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          {sidebarOpen ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
        </button>
      )}

      {/* ── Main content area ─────────────────────────────────────────────────── */}
      <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 50,
          height: 48, padding: isMobile ? '0 12px' : '0 20px',
          display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8,
          background: 'var(--toolbar-bg)',
          borderBottom: '1px solid var(--border)',
          backdropFilter: 'blur(12px)',
        }}>
          {/* Breadcrumb nav — owner only; read-only visitors are locked to their entry level */}
          {isOwner && isMobile ? (
            <>
              <button onClick={async () => {
                const operation = beginNavigation();
                const boxes = await fetchBoxes(null);
                if (operation !== navigationOperation.current) return;
                setLevelStack([]); setCurrentBoxes(boxes);
                navigateLevel([]);
                finishNavigation(operation);
              }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 4 }}>
                <Home size={16} />
              </button>
              {levelStack.length > 0 && (
                <>
                  <span style={{ color: 'var(--text-faint)', fontSize: 12 }}>›</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                    {levelStack[levelStack.length - 1].box.title}
                  </span>
                </>
              )}
            </>
          ) : isOwner ? (
            <>
              {levelStack.length > 0 && (
                <button onClick={async () => {
                  const operation = beginNavigation();
                  const boxes = await fetchBoxes(null);
                  if (operation !== navigationOperation.current) return;
                  setLevelStack([]); setCurrentBoxes(boxes);
                  navigateLevel([]);
                  finishNavigation(operation);
                }} style={crumbBtn(false)}>
                  {plan.title}
                </button>
              )}
              {levelStack.map((entry, i) => (
                <React.Fragment key={entry.box.id}>
                  <span style={{ color: 'var(--text-faint)', fontSize: 12 }}>›</span>
                  <button
                    onClick={async () => {
                      if (i === levelStack.length - 1) return;
                      const operation = beginNavigation();
                      const newStack = levelStack.slice(0, i + 1);
                      const pid = levelStack[i].box.id;
                      const refreshed = await fetchBoxes(pid);
                      if (operation !== navigationOperation.current) return;
                      setLevelStack(newStack); setCurrentBoxes(refreshed);
                      navigateLevel(newStack);
                      finishNavigation(operation);
                    }}
                    style={crumbBtn(i === levelStack.length - 1)}
                  >
                    {entry.box.title}
                  </button>
                </React.Fragment>
              ))}
            </>
          ) : null}

          <div style={{ flex: 1 }} />

          {/* Mobile sidebar button */}
          {isMobile && (
            <button
              onClick={() => setMobileSidebarOpen(true)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
                padding: 6, borderRadius: 8,
              }}
              title="Outline"
            >
              <LayoutList size={18} />
            </button>
          )}

          {isOwner && (
            <button
              onClick={() => setEditing({ _parentId: parentBox?.id || 'root' })}
              style={{
                background: color, border: 'none', borderRadius: 8,
                padding: isMobile ? '5px 10px' : '5px 14px',
                cursor: 'pointer', color: 'white',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              {isMobile ? '+' : '+ Add card'}
            </button>
          )}
        </div>

        <CardSearch boxes={allBoxes} planTitle={plan.title} color={color} onSelect={navigateToBox} isMobile={isMobile} open={searchOpen} setOpen={setSearchOpen} triggerRef={searchButtonRef} />

        {/* ── Mobile sidebar overlay ─────────────────────────────────────────── */}
        {isMobile && mobileSidebarOpen && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 500,
            background: 'var(--bg-2)',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Outline</span>
              <button
                onClick={() => setMobileSidebarOpen(false)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', display: 'flex', padding: 4,
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Tab bar */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              {[{ id: 'outline', label: 'Outline', Icon: Home }, { id: 'todo', label: 'TODO', Icon: ListTodo }].map(({ id, label, Icon }) => {
                const active = (sidebarTab || 'outline') === id;
                const todoCount = id === 'todo' ? allBoxes.filter(b => b.is_todo).length : 0;
                return (
                  <button key={id} onClick={() => setSidebarTab(id)} style={{
                    flex: 1, padding: '11px 4px', border: 'none', background: 'transparent',
                    borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 6, fontFamily: 'inherit',
                    color: active ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontWeight: active ? 700 : 500, fontSize: 13,
                  }}>
                    <Icon size={14} />
                    {label}
                    {id === 'todo' && todoCount > 0 && (
                      <span style={{
                        background: color, color: '#fff', borderRadius: 8,
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', lineHeight: 1.6,
                      }}>{todoCount}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 6px' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={({ active }) => { _outlineDropPosRef.current = null; _outlineDropPosRef.targetId = null; setOutlineDraggingId(active.id); }}
                onDragEnd={handleOutlineDragEnd}
                onDragCancel={() => { _outlineDropPosRef.current = null; _outlineDropPosRef.targetId = null; setOutlineDraggingId(null); }}
              >
                {(sidebarTab || 'outline') === 'outline' && (() => {
                  const rootBoxes = allBoxes.filter(b => !b.parent_id || b.parent_id === 'root');
                  return (
                    <>
                      <div
                        onClick={() => { navigateToBox(null); setMobileSidebarOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                          background: currentPath.length === 0 ? 'var(--bg-4)' : 'transparent',
                          marginBottom: 4,
                        }}
                      >
                        <Home size={15} style={{ color: currentPath.length === 0 ? 'var(--text-primary)' : 'var(--text-muted)' }} />
                        <span style={{
                          fontSize: 14, fontWeight: currentPath.length === 0 ? 700 : 600,
                          color: currentPath.length === 0 ? 'var(--text-primary)' : 'var(--text-muted)',
                        }}>{plan.title}</span>
                      </div>
                      {isOwner && !(plan.role === 'editor' && plan.has_private_cards) && <SummaryOutlineLink onOpenSummary={() => { setMobileSidebarOpen(false); onOpenSummary(); }} summaryStatus={summaryStatus} color={color} mobile />}
                      <div style={{ height: 1, background: 'var(--border)', margin: '4px 8px 8px' }} />
                      {rootBoxes.map(box => (
                        <OutlineNode
                          key={box.id}
                          box={box}
                          allBoxes={allBoxes}
                          depth={0}
                          currentPath={currentPath}
                          onNavigate={(b) => { navigateToBox(b); setMobileSidebarOpen(false); }}
                          onMoveBox={handleOutlineDragEnd}
                          draggingId={outlineDraggingId}
                          isOwner={isOwner}
                          isMobileDevice={true}
                        />
                      ))}
                    </>
                  );
                })()}

                {sidebarTab === 'todo' && (() => {
                  const todos = allBoxes.filter(b => b.is_todo);
                  if (todos.length === 0) return (
                    <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                      <ListTodo size={28} style={{ color: 'var(--text-faint)', margin: '0 auto 10px', display: 'block' }} />
                      <div style={{ fontSize: 14, color: 'var(--text-faint)' }}>No TODO items yet</div>
                    </div>
                  );
                  return todos.map(box => {
                    const parent = box.parent_id ? allBoxes.find(b => b.id === box.parent_id) : null;
                    return (
                      <div key={box.id} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                        margin: '2px 4px',
                      }}
                        onClick={() => { navigateToBox(box); setMobileSidebarOpen(false); }}
                      >
                        {isOwner && (
                          <button onClick={e => { e.stopPropagation(); handleTodoToggle(box); }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color, flexShrink: 0, marginTop: 2 }}>
                            <CheckSquare size={16} />
                          </button>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {box.title || 'Untitled'}
                          </div>
                          {parent && (
                            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>in {parent.title || 'Untitled'}</div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </DndContext>
            </div>
          </div>
        )}

        {uploadNotice && <div role="status" style={{ position: 'absolute', zIndex: 30, top: 54, left: '50%', transform: 'translateX(-50%)', padding: '9px 12px', borderRadius: 9, background: 'var(--toolbar-bg)', color: 'var(--text-primary)', boxShadow: '0 2px 12px #0002', fontSize: 12 }}>{uploadNotice}</div>}
        {lockNotice && <div role="alert" style={{ position: 'absolute', zIndex: 30, top: 54, left: '50%', transform: 'translateX(-50%)', maxWidth: 'min(90vw, 520px)', padding: '9px 12px', borderRadius: 9, background: '#fef3c7', color: '#78350f', boxShadow: '0 2px 12px #0002', fontSize: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{lockNotice}</span><button aria-label="Dismiss notice" onClick={() => setLockNotice('')} style={{ border: 0, background: 'none', cursor: 'pointer' }}>✕</button>
        </div>}

        {/* Animated level */}
        <AnimatePresence mode="wait">
          <motion.div
            key={levelKey}
            initial={{ opacity: 0, x: goingDeeper ? 60 : -60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: goingDeeper ? -60 : 60 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            onDragOver={onCanvasDragOver}
            onDragLeave={onCanvasDragLeave}
            onDrop={onCanvasDrop}
            style={{
              position: 'absolute', inset: 0, top: 48,
              overflowY: 'auto', overflowX: 'hidden',
              padding: isMobile ? '16px 12px 24px' : '32px 48px 48px',
              outline: dropOver ? `3px dashed ${color}` : 'none',
              outlineOffset: '-6px',
              background: dropOver ? `${color}08` : undefined,
              transition: 'outline 0.15s, background 0.15s',
            }}
          >
            {/* Drop overlay hint */}
            {dropOver && (
              <div style={{
                position: 'fixed', inset: 0, zIndex: 999, pointerEvents: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  padding: '16px 28px', borderRadius: 14, background: 'var(--toolbar-bg)',
                  backdropFilter: 'blur(12px)', border: `2px dashed ${color}`,
                  fontSize: 14, fontWeight: 700, color,
                }}>
                  Drop to create a card
                </div>
              </div>
            )}
            {/* Parent card — full width hero above children grid */}
            {parentBox && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                style={{ marginBottom: compactView ? 16 : 28 }}
              >
                {/* Back button */}
                {(!shareRootId || levelStack.length > 1) && <div
                  onClick={e => { e.stopPropagation(); goBack(); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '5px 12px', marginBottom: 12,
                    background: 'var(--bg-3)', border: `1px solid ${color}40`,
                    borderRadius: 8, cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  <span style={{ fontSize: 15, color, lineHeight: 1 }}>←</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>Back</span>
                </div>}
                {/* Parent card full-width */}
                <div style={{ borderLeft: '4px solid #16a34a', borderRadius: 14, overflow: 'hidden' }}>
                  <BoxCard
                    box={parentBox}
                    depth={levelStack.length - 1}
                    index={0}
                    color={color}
                    isMobile={false}
                    onDrillIn={null}
                    isCurrentLevel
                    onEdit={isOwner ? () => beginEdit(parentBox) : null}
                    canSetPrivate={plan.role === 'owner'}
                    lockedBy={cardLocks[parentBox.id]}
                    onDelete={null}
                    onUpdate={(updated) => {
                      setLevelStack(prev => prev.map((l, i) =>
                        i === prev.length - 1 ? { ...l, box: updated } : l
                      ));
                      setAllBoxes(prev => prev.map(b => b.id === updated.id ? updated : b));
                    }}
                    onAskAI={() => aiSidebarRef?.current?.openWithContext(parentBox)}
                    hideZoomIn
                    compactView={compactView}
                  />
                </div>
              </motion.div>
            )}

            {/* Card grid */}
            {currentBoxes.length > 0 ? (
              <>
              {isMobile ? (
                /* Mobile: plain grid, no drag-to-reorder */
                <div style={{ display: 'flex', flexDirection: 'column', gap: CARD_GAP }}>
                  {currentBoxes.map((box, i) => (
                    <BoxCard
                      key={box.id}
                      box={box} index={i} color={color} isMobile={true}
                      compactView={compactView}
                      onDrillIn={() => drillInto(box)}
                      onEdit={isOwner ? () => beginEdit(box) : null}
                      canSetPrivate={plan.role === 'owner'}
                      lockedBy={cardLocks[box.id]}
                      onDelete={isOwner ? () => handleDelete(box.id) : null}
                      onUpdate={(updated) => { setCurrentBoxes(prev => prev.map(b => b.id === updated.id ? updated : b)); setAllBoxes(prev => prev.map(b => b.id === updated.id ? updated : b)); }}
                      onAskAI={isOwner ? () => aiSidebarRef?.current?.openWithContext(box) : null}
                    />
                  ))}
                </div>
              ) : (
                /* Desktop: sortable grid with drag-to-reorder */
                <DndContext
                  sensors={sensors}
                  collisionDetection={pointerWithin}
                  onDragStart={e => setActiveId(e.active.id)}
                  onDragEnd={handleDragEnd}
                  onDragCancel={() => setActiveId(null)}
                >
                  <SortableContext items={currentBoxes.map(b => b.id)} strategy={rectSortingStrategy}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                      gap: CARD_GAP,
                      alignItems: 'start',
                    }}>
                      {currentBoxes.map((box, i) => (
                        <SortableCard
                          key={box.id}
                          box={box} index={i} color={color} isMobile={false}
                          compactView={compactView}
                          onDrillIn={() => drillInto(box)}
                          onEdit={isOwner ? () => beginEdit(box) : null}
                          canSetPrivate={plan.role === 'owner'}
                          lockedBy={cardLocks[box.id]}
                          onDelete={isOwner ? () => handleDelete(box.id) : null}
                          onUpdate={(updated) => { setCurrentBoxes(prev => prev.map(b => b.id === updated.id ? updated : b)); setAllBoxes(prev => prev.map(b => b.id === updated.id ? updated : b)); }}
                          onToggleSpan={isOwner ? () => toggleSpan(box) : null}
                          onAskAI={isOwner ? () => aiSidebarRef?.current?.openWithContext(box) : null}
                        />
                      ))}
                      {/* Add tile — desktop, owner only */}
                      {isOwner && (
                        <div>
                          <button
                            onClick={() => setEditing({ _parentId: parentBox?.id || 'root' })}
                            style={{
                              width: '100%', minHeight: 80, background: 'transparent',
                              border: `2px dashed ${color}50`, borderRadius: 14,
                              cursor: 'pointer', color, fontFamily: 'inherit',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              gap: 6, fontSize: 13, fontWeight: 600,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = `${color}08`}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            + Add card
                          </button>
                        </div>
                      )}
                    </div>
                  </SortableContext>
                  <DragOverlay>
                    {activeId && (() => {
                      const box = currentBoxes.find(b => b.id === activeId);
                      return box ? (
                        <div style={{ opacity: 0.85, transform: 'rotate(1deg)', pointerEvents: 'none' }}>
                          <BoxCard box={box} depth={0} index={0} color={color} isMobile={false} compactView={compactView}
                            onDrillIn={() => {}} onEdit={() => {}} onDelete={() => {}} />
                        </div>
                      ) : null;
                    })()}
                  </DragOverlay>
                </DndContext>
              )}
              </>
            ) : (
              <div style={{ paddingTop: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                {isOwner && (
                  <button
                    onClick={() => setEditing({ _parentId: parentBox?.id || 'root' })}
                    style={{ background: color, border: 'none', borderRadius: 12, padding: '12px 24px', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    + Add card
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {editing && (
        <BoxEditor
          key={editing?.id || 'new'}
          box={editing?.id ? editing : null}
          depth={levelStack.length}
          color={color}
          onSave={handleSave}
          error={editError}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

const crumbBtn = (active) => ({
  background: 'none', border: 'none', padding: '2px 4px',
  cursor: active ? 'default' : 'pointer',
  color: active ? 'var(--text-primary)' : 'var(--text-muted)',
  fontSize: 13, fontWeight: active ? 700 : 400, fontFamily: 'inherit',
});
